// Dispatch table: maps "family.action" event names to cache-patcher functions.
// Each handler receives a parsed Frame and the QueryClient — handlers
// perform targeted invalidations or setQueryData patches so UI updates
// without a full refetch cascade.
//
// Reference doc: docs/realtime-event-map.md — keep that doc in sync when you
// add or remove an entry below.
//
// Root query key strings here MUST match what the hooks use as queryKey[0]:
//   - "kg-graph"            (all KG keys share this root via kgGraphKeys.all)
//   - "media.bulk.jobs.active" (bulk active-job list)
//   - "media.bulk.job"      (per-job detail + items)
//   - "media.run"           (per-run media cache)
//   - "media.run.live-state" (per-run streaming state for useMediaJobEvents)
//   - "media"               (asset list — invalidated by asset events)
//   - "permissions"         (resource-permission cache)
//
// On-reopen invalidate-all (Red-Team H5): allRealtimeQueryKeys enumerates
// every root key this table may affect. The provider sweeps this list on
// reconnect so stale frames from a ring-buffer gap never leave the UI stuck.
import type { QueryClient } from "@tanstack/react-query";
import { showToast } from "@/lib/toast";
import type { Frame, DispatchHandler } from "./types";
import {
  handleMediaAssetEvent,
  handleMediaKBIngest,
  handleMediaRunCreated,
  handleMediaRunStarted,
  handleMediaRunEncodeStarted,
  handleMediaRunEncodeCompleted,
  handleMediaRunPartProgress,
  handleMediaRunTranscribeStarted,
  handleMediaRunTranscribeCompleted,
  handleMediaRunRefineStarted,
  handleMediaRunRefineCompleted,
  handleMediaRunCompleted,
  handleMediaRunFailed,
  handleMediaRunRetried,
} from "./dispatch-media-handlers";
import {
  handleWorkflowRunQueued,
  handleWorkflowRunStarted,
  handleWorkflowRunCompleted,
  handleWorkflowRunFailed,
  handleWorkflowRunCancelled,
  handleWorkflowRunPaused,
  handleWorkflowRunResumed,
  handleWorkflowRunCostAborted,
  handleWorkflowRunReaped,
  handleWorkflowRunPoisonPill,
  handleWorkflowRunRetried,
  handleWorkflowStepStarted,
  handleWorkflowStepCompleted,
  handleWorkflowStepFailed,
  handleWorkflowStepSkipped,
  handleWorkflowStepProgress,
  handleWorkflowStepTextComplete,
  handleWorkflowTokenDelta,
  handleWorkflowAgentDecisionMade,
  handleWorkflowAgentToolCallStart,
  handleWorkflowAgentToolCallResult,
  handleWorkflowCheckpointCreated,
  handleWorkflowCheckpointRequested,
  handleWorkflowCheckpointApproved,
  handleWorkflowCheckpointRejected,
  handleWorkflowCheckpointExpired,
  handleWorkflowCheckpointCancelled,
  handleWorkflowCheckpointResumeFailed,
} from "./dispatch-workflow-handlers";

// ── helpers ──────────────────────────────────────────────────────────────────

function invalidate(qc: QueryClient, ...queryKey: unknown[]) {
  qc.invalidateQueries({ queryKey });
}

// ── KG handlers ───────────────────────────────────────────────────────────────

function handleKgRunProgress(_frame: Frame, qc: QueryClient) {
  invalidate(qc, "kg-graph", "extraction-runs-active");
}

function handleKgExtractStarted(frame: Frame, qc: QueryClient) {
  invalidate(qc, "kg-graph", "extraction-runs-active");
  patchMediaKGStatus(qc, frame, "extracting");
}

function handleKgExtractCompleted(frame: Frame, qc: QueryClient) {
  invalidate(qc, "kg-graph", "extraction-runs-active");
  invalidate(qc, "kg-graph", "analyzer-status");
  invalidate(qc, "kg-graph", "top");
  invalidate(qc, "kg-graph", "summary");
  invalidate(qc, "kg-graph", "entity-detail");
  const data = frame.data as Record<string, unknown> | null;
  const entityCount = typeof data?.entity_count === "number" ? data.entity_count : undefined;
  patchMediaKGStatus(qc, frame, "extracted", entityCount);
}

function handleKgExtractFailed(frame: Frame, qc: QueryClient) {
  invalidate(qc, "kg-graph", "extraction-runs-active");
  invalidate(qc, "kg-graph", "analyzer-status");
  invalidate(qc, "kg-graph", "dlq-list");
  invalidate(qc, "kg-graph", "top");
  invalidate(qc, "kg-graph", "summary");
  invalidate(qc, "kg-graph", "entity-detail");
  patchMediaKGStatus(qc, frame, "failed");
}

function handleKgDlqChanged(_frame: Frame, qc: QueryClient) {
  invalidate(qc, "kg-graph", "dlq-list");
}

function handleKgLouvainRunCompleted(_frame: Frame, qc: QueryClient) {
  invalidate(qc, "kg-graph", "analyzer-status");
  invalidate(qc, "kg-graph", "runs");
  invalidate(qc, "kg-graph", "top");
  invalidate(qc, "kg-graph", "summary");
  invalidate(qc, "kg-graph", "entity-detail");
}

type MediaKGRow = {
  id?: string | null;
  kbDocId?: string | null;
  kgExtractStatus?: string | null;
  kgEntityCount?: number | null;
} & Record<string, unknown>;

type MediaListSnapshot = {
  data?: MediaKGRow[];
} & Record<string, unknown>;

type MediaDetailSnapshot = {
  data?: MediaKGRow;
} & Record<string, unknown>;

function patchMediaKGStatus(
  qc: QueryClient,
  frame: Frame,
  status: "extracting" | "extracted" | "failed",
  entityCount?: number,
) {
  const data = frame.data as Record<string, unknown> | null;
  const knowledgeId = stringPayloadField(data, "knowledge_id", "knowledgeId");
  const assetId = stringPayloadField(data, "asset_id", "assetId");
  if (!knowledgeId && !assetId) {
    invalidateMediaKGQueries(qc);
    return;
  }

  qc.setQueriesData<MediaListSnapshot | undefined>(
    { queryKey: ["media", "list"] },
    (snapshot) => {
      if (!snapshot?.data) return snapshot;
      let touched = false;
      const nextData = snapshot.data.map((row) => {
        if (!matchesMediaKGRow(row, knowledgeId, assetId)) return row;
        touched = true;
        return patchMediaKGRow(row, status, entityCount);
      });
      return touched ? { ...snapshot, data: nextData } : snapshot;
    },
  );
  qc.setQueriesData<MediaDetailSnapshot | undefined>(
    { queryKey: ["media", "detail"] },
    (snapshot) => {
      if (!snapshot?.data || !matchesMediaKGRow(snapshot.data, knowledgeId, assetId)) {
        return snapshot;
      }
      return { ...snapshot, data: patchMediaKGRow(snapshot.data, status, entityCount) };
    },
  );
  invalidateMediaKGQueries(qc);
}

function stringPayloadField(data: Record<string, unknown> | null, snake: string, camel: string) {
  const snakeValue = data?.[snake];
  if (typeof snakeValue === "string" && snakeValue !== "") return snakeValue;
  const camelValue = data?.[camel];
  if (typeof camelValue === "string" && camelValue !== "") return camelValue;
  return null;
}

function matchesMediaKGRow(row: MediaKGRow, knowledgeId: string | null, assetId: string | null) {
  return (knowledgeId != null && row.kbDocId === knowledgeId) || (assetId != null && row.id === assetId);
}

function invalidateMediaKGQueries(qc: QueryClient) {
  invalidate(qc, "media", "list");
  invalidate(qc, "media", "detail");
}

function patchMediaKGRow(
  row: MediaKGRow,
  status: "extracting" | "extracted" | "failed",
  entityCount?: number,
): MediaKGRow {
  return {
    ...row,
    kgExtractStatus: status,
    ...(entityCount != null ? { kgEntityCount: entityCount } : {}),
  };
}

// ── Bulk handlers ─────────────────────────────────────────────────────────────

function handleBulkProgress(frame: Frame, qc: QueryClient) {
  invalidate(qc, "media.bulk.jobs.active");
  const data = frame.data as Record<string, unknown> | null;
  const bulkJobId = typeof data?.bulk_job_id === "string" ? data.bulk_job_id : null;
  if (bulkJobId) invalidate(qc, "media.bulk.job", bulkJobId);
}

function handleBulkCompleted(frame: Frame, qc: QueryClient) {
  invalidate(qc, "media.bulk.jobs.active");
  const data = frame.data as Record<string, unknown> | null;
  const bulkJobId = typeof data?.bulk_job_id === "string" ? data.bulk_job_id : null;
  if (bulkJobId) invalidate(qc, "media.bulk.job", bulkJobId);
  const title = typeof data?.title === "string" ? data.title : "";
  showToast.success({ title: title ? `Bulk job "${title}" complete` : "Bulk job complete" });
}

function handleBulkDigestRequested(frame: Frame, qc: QueryClient) {
  invalidate(qc, "media.bulk.jobs.active");
  const data = frame.data as Record<string, unknown> | null;
  const bulkJobId = typeof data?.bulk_job_id === "string" ? data.bulk_job_id : null;
  if (bulkJobId) invalidate(qc, "media.bulk.job", bulkJobId);
}

// ── Media run progress (legacy shim for pubsub-side media.run.progress) ───────

function handleMediaRunProgress(frame: Frame, qc: QueryClient) {
  const data = frame.data as Record<string, unknown> | null;
  const runId = typeof data?.run_id === "string" ? data.run_id : null;
  if (!runId) return;
  const existing = qc.getQueryState(["media.run", runId]);
  if (existing) {
    qc.setQueryData(["media.run", runId], (prev: unknown) => {
      if (!prev || typeof prev !== "object") return prev;
      return { ...(prev as Record<string, unknown>), ...data };
    });
  } else {
    invalidate(qc, "media.run", runId);
  }
}

// ── Permission handler ────────────────────────────────────────────────────────
// Replaces usePermissionInvalidationStream (legacy /sse/permissions endpoint).
// The multiplexed hub now subscribes to perm.invalidate and emits
// permission.invalidated frames so every org-connected tab gets cache flush.

function handlePermissionInvalidated(_frame: Frame, qc: QueryClient) {
  qc.invalidateQueries({ queryKey: ["permissions"] });
  if (import.meta.env.DEV) {

    console.debug("[realtime] permission.invalidated — flushed permissions cache");
  }
}

// ── Auth handler ──────────────────────────────────────────────────────────────
// Provider intercepts "auth.expiring" for JWT refresh; this is a safety-net
// no-op so the dispatch loop does not warn on a known event.

function handleAuthExpiring(_frame: Frame, _qc: QueryClient) { /* intercepted by provider */ }

// ── dispatch table ────────────────────────────────────────────────────────────

export const dispatchTable: Record<string, DispatchHandler> = {
  // KG
  "kg.run.progress": handleKgRunProgress,
  "kg.extract.started": handleKgExtractStarted,
  "kg.extract.completed": handleKgExtractCompleted,
  "kg.extract.failed": handleKgExtractFailed,
  "kg.louvain.run.completed": handleKgLouvainRunCompleted,
  "kg.dlq.changed": handleKgDlqChanged,
  // Bulk
  "bulk.digest.requested": handleBulkDigestRequested,
  "bulk.progress": handleBulkProgress,
  "bulk.completed": handleBulkCompleted,
  // Media — legacy shim
  "media.run.progress": handleMediaRunProgress,
  // Media — 16 canonical transcription lifecycle event names
  "media.asset.uploaded": handleMediaAssetEvent,
  "media.asset.deleted": handleMediaAssetEvent,
  "media.processing.run.created": handleMediaRunCreated,
  "media.processing.run.started": handleMediaRunStarted,
  "media.processing.run.encode_started": handleMediaRunEncodeStarted,
  "media.processing.run.encode_completed": handleMediaRunEncodeCompleted,
  "media.processing.run.part_progress": handleMediaRunPartProgress,
  "media.processing.run.transcribe_started": handleMediaRunTranscribeStarted,
  "media.processing.run.transcribe_completed": handleMediaRunTranscribeCompleted,
  "media.processing.run.refine_started": handleMediaRunRefineStarted,
  "media.processing.run.refine_completed": handleMediaRunRefineCompleted,
  "media.processing.run.completed": handleMediaRunCompleted,
  "media.processing.run.failed": handleMediaRunFailed,
  "media.processing.run.retried": handleMediaRunRetried,
  "media.kb_ingest.queued": handleMediaKBIngest,
  "media.kb_ingest.completed": handleMediaKBIngest,
  // Permission invalidation (replaces /sse/permissions endpoint)
  "permission.invalidated": handlePermissionInvalidated,
  // Auth
  "auth.expiring": handleAuthExpiring,
  // Workflow — 27 events. Dispatch keys use DB dot.case verbatim (Q10 LOCKED).
  // EXCEPTION: workflow.token_delta uses snake (matches hub.go:200 skip-ring check).
  // Run lifecycle (7)
  "workflow.run.queued": handleWorkflowRunQueued,
  "workflow.run.started": handleWorkflowRunStarted,
  "workflow.run.completed": handleWorkflowRunCompleted,
  "workflow.run.failed": handleWorkflowRunFailed,
  "workflow.run.cancelled": handleWorkflowRunCancelled,
  "workflow.run.paused": handleWorkflowRunPaused,
  "workflow.run.resumed": handleWorkflowRunResumed,
  // Step lifecycle (5)
  "workflow.step.started": handleWorkflowStepStarted,
  "workflow.step.completed": handleWorkflowStepCompleted,
  "workflow.step.failed": handleWorkflowStepFailed,
  "workflow.step.skipped": handleWorkflowStepSkipped,
  "workflow.step.progress": handleWorkflowStepProgress,
  // Agentic (5)
  "workflow.step.text_complete": handleWorkflowStepTextComplete,
  "workflow.token_delta": handleWorkflowTokenDelta, // snake exception — hub skip-ring check
  "workflow.agent.decision_made": handleWorkflowAgentDecisionMade,
  "workflow.agent.tool_call_start": handleWorkflowAgentToolCallStart,
  "workflow.agent.tool_call_result": handleWorkflowAgentToolCallResult,
  // Checkpoint (7)
  "workflow.checkpoint.created": handleWorkflowCheckpointCreated,
  "workflow.checkpoint.requested": handleWorkflowCheckpointRequested,
  "workflow.checkpoint.approved": handleWorkflowCheckpointApproved,
  "workflow.checkpoint.rejected": handleWorkflowCheckpointRejected,
  "workflow.checkpoint.expired": handleWorkflowCheckpointExpired,
  "workflow.checkpoint.cancelled": handleWorkflowCheckpointCancelled,
  "workflow.checkpoint.resume_failed": handleWorkflowCheckpointResumeFailed,
  // Maintenance / cost (4)
  "workflow.run.cost_aborted": handleWorkflowRunCostAborted,
  "workflow.run.reaped": handleWorkflowRunReaped,
  "workflow.run.poison_pill": handleWorkflowRunPoisonPill,
  "workflow.run.retried": handleWorkflowRunRetried,
};

/** Default handler for unknown events — logs in dev, never throws. */
export function defaultDispatch(eventName: string, frame: Frame) {
  if (import.meta.env.DEV) {

    console.debug("[realtime] unhandled event", eventName, frame);
  }
}

// ── realtime query key allowlist (Red-Team H5) ────────────────────────────────

/**
 * Root query keys managed by the realtime dispatch table.
 * On-reconnect, the provider invalidates all queries whose key[0] is in this
 * set so ring-buffer gaps never leave the UI reading stale state.
 */
export const allRealtimeQueryKeys: readonly string[] = [
  "kg-graph",
  "media.bulk.jobs.active",
  "media.bulk.job",
  "media.run",
  "media.run.live-state",
  "media",
  "permissions",
  "workflows",
] as const;
